import React from 'react';
import { UI_CONSTANTS } from '../../constants/ui';

interface InfoBadgeProps {
  icon: React.ReactNode;
  text: string;
  variant?: 'slate' | 'emerald';
}

interface CompactHeaderProps {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  infoBadges?: InfoBadgeProps[];
  actions?: React.ReactNode[];
  className?: string;
}

const InfoBadge: React.FC<InfoBadgeProps> = ({ icon, text, variant = 'slate' }) => {
  const bgClass = variant === 'emerald' 
    ? 'bg-emerald-50 dark:bg-emerald-900/30' 
    : 'bg-slate-100 dark:bg-slate-800/60';
  const textClass = variant === 'emerald'
    ? 'text-emerald-600 dark:text-emerald-300'
    : 'text-slate-600 dark:text-slate-300';
  const iconClass = variant === 'emerald'
    ? 'text-emerald-500 dark:text-emerald-400'
    : 'text-slate-500 dark:text-slate-400';

  return (
    <div className={`flex items-center gap-1 px-1.5 py-0.5 ${bgClass} rounded text-xs`}>
      <div className={`w-2.5 h-2.5 ${iconClass}`}>
        {icon}
      </div>
      <span className={`${textClass} font-medium`}>{text}</span>
    </div>
  );
};

export const CompactHeader: React.FC<CompactHeaderProps> = ({ 
  title, 
  subtitle,
  icon, 
  infoBadges = [],
  actions = [],
  className = '' 
}) => {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm ${className}`}>
      <div className="px-2.5 sm:px-4 py-2 sm:py-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <div className={`w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br ${UI_CONSTANTS.COLORS.slate.gradient} rounded-md flex items-center justify-center flex-shrink-0`}>
                <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white">
                  {icon}
                </div>
              </div>
              <span className="truncate">{title}</span>
            </h1>
            {(subtitle || infoBadges.length > 0) && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {subtitle && (
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {subtitle}
                  </p>
                )}
                {infoBadges.map((badge) => (
                  <InfoBadge key={badge.text} {...badge} />
                ))}
              </div>
            )}
          </div>
          {actions.length > 0 && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompactHeader;